import { capitalize } from "@mui/material";
import { Link } from "@tanstack/react-router";
import {
  Add,
  BubbleText,
  Button,
  ContextualMenu,
  Search,
  Text,
} from "@totalenergiescode/mobility-business-rev-design-system";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { RoundedTopLeftContainer } from "@/components/containers/RoundedTopLeftContainer";
import { RolesProfileIcon } from "@/components/shared/RolesProfileIcon";
import { TabMenuButton } from "@/components/shared/TabMenuButton";
import EditUser from "@/components/UserForm/Edit/component";
import { useAddSnackbar } from "@/contexts/snackbar";

import { getStatus } from "./helper";
import { generateUsers } from "./mock-data";
import {
  StyledCell,
  StyledCellContextualMenu,
  StyledCellHeaderText,
  StyledCellText,
  StyledCustomTableContainer,
  StyledDivider,
  StyledPageHeader,
  StyledRowContainerLink,
  StyledSearchField,
  StyledSearchFieldContainer,
  StyledTable,
  StyledTableHeader,
  StyledTabMenuContainer,
  StyledTitle,
} from "./styles";

const BackOffice = () => {
  const { t: tUsers } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(-1);
  const [selectedTab, setTabSelected] = useState(0);
  const listOfAgentsGenerated = generateUsers(50);
  const [listOfAgents, setListOfAgents] = useState(listOfAgentsGenerated);
  const breadcrumbItems = [
    {
      title: tUsers("common.features.dashboard"),
      path: "/" as const,
    },
    {
      title: tUsers("users.forms.backOfficeAgents"),
      path: "/backoffice-users" as const,
    },
  ];

  const addSnackbar = useAddSnackbar();

  const getLinks = (status: string, email: string) => {
    switch (status) {
      case "active":
        return [
          {
            text: tUsers("users.actions.edit"),
            size: "s",
            textAlign: "left",
            className: "customMenuItem",
            onClick: () => {
              setOpen(true);
            },
          },
          {
            text: tUsers("users.actions.deactivate"),
            size: "s",
            color: "red",
            textAlign: "left",
            onClick: () => {
              addSnackbar({
                type: "success",
                title: tUsers("users.actions.deactivated"),
                message: "",
                isAutoClose: true,
              });
            },
          },
        ];
      case "inactive":
        return [
          {
            text: tUsers("users.actions.reactivate"),
            size: "s",
            textAlign: "left",
            onClick: () => {
              addSnackbar({
                type: "success",
                title: tUsers("users.actions.reactivated"),
                message: "",
                isAutoClose: true,
              });
            },
          },
        ];
      case "inviting":
        return [
          {
            text: tUsers("users.actions.reinvite"),
            size: "s",
            textAlign: "left",
            className: "customMenuItem",
            onClick: () => {
              addSnackbar({
                type: "success",
                title: tUsers("users.actions.reinvited"),
                message: tUsers("users.actions.reinvitedDescription", {
                  email,
                }),
                isAutoClose: true,
              });
            },
          },
          {
            text: tUsers("users.actions.deactivate"),
            size: "s",
            textAlign: "left",
            color: "red",
            onClick: () => {
              addSnackbar({
                type: "success",
                title: tUsers("users.actions.cancelInvitation"),
                message: tUsers("users.actions.cancelInvitationDescription", {
                  email,
                }),
                isAutoClose: true,
              });
            },
          },
        ];
      default:
        return [];
    }
  };

  const tabMenuContent = [
    {
      label: "All",
      dataTestId: "tab-menu-button",
      hasBubbleText: true,
      isBadge: false,
      text: listOfAgentsGenerated.length.toString(),
      variant: "grey",
      onClick: () => {
        setListOfAgents(listOfAgentsGenerated);
      },
    },
    {
      label: "Active",
      dataTestId: "tab-menu-button",
      hasBubbleText: true,
      isBadge: false,
      text: listOfAgentsGenerated
        .filter((agent) => agent.status === "active")
        .length.toString(),
      variant: "green",
      onClick: () => {
        setListOfAgents(
          listOfAgentsGenerated.filter((agent) => agent.status === "active"),
        );
      },
    },
    {
      label: "Invitation sent",
      dataTestId: "tab-menu-button",
      hasBubbleText: true,
      isBadge: false,
      text: listOfAgentsGenerated
        .filter((agent) => agent.status === "inviting")
        .length.toString(),
      variant: "orange",
      onClick: () => {
        setListOfAgents(
          listOfAgentsGenerated.filter((agent) => agent.status === "inviting"),
        );
      },
    },
    {
      label: "Disabled",
      dataTestId: "tab-menu-button",
      hasBubbleText: true,
      isBadge: false,
      text: listOfAgentsGenerated
        .filter((agent) => agent.status === "inactive")
        .length.toString(),
      variant: "grey",
      onClick: () => {
        setListOfAgents(
          listOfAgentsGenerated.filter((agent) => agent.status === "inactive"),
        );
      },
    },
  ];

  return (
    <>
      <StyledPageHeader
        breadcrumbItems={breadcrumbItems}
        title={tUsers("users.forms.backOfficeAgents")}
        href={""}
        rightContent={
          <Link to='/backoffice-users/add'>
            <Button
              leftIcon={<Add width={25} height={25} color='white' />}
              variant='primary'
              isLowercase
              buttonText={tUsers("users.forms.addAgent")}
              onClick={() => {}}
            />
          </Link>
        }
      />
      <RoundedTopLeftContainer>
        <EditUser
          isOpen={open}
          onClose={() => setOpen(false)}
          userData={{
            email: "test@example.com",
            familyName: "Doe",
            iGG: "123456",
            name: "John",
            profile: "agent",
            function: "Sales",
          }}
        />
        <StyledTitle variant='title3' fontWeight='medium'>
          List of internal Agents
        </StyledTitle>
        <StyledTabMenuContainer>
          {tabMenuContent.map((tab: any, index: number) => (
            <TabMenuButton
              content={tab}
              index={index}
              isSelectedTab={index === selectedTab}
              setTabSelected={() => setTabSelected(index)}
              dataTestId='tab-menu-button'
            />
          ))}
        </StyledTabMenuContainer>

        <StyledDivider />
        <StyledSearchFieldContainer>
          <StyledSearchField
            dataTestId='text-field'
            onChange={(_e) => {}}
            placeholder={tUsers("companies.list.searchPlaceholder")}
            startIcon={<Search width={18} height={18} />}
            value={""}
          />
        </StyledSearchFieldContainer>
        <StyledCustomTableContainer>
          <StyledTableHeader>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("companies.list.data.email")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("companies.list.data.name")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("IGG")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("companies.list.data.accountProfile")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("companies.list.data.status")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("companies.list.data.createdAt")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>
                {tUsers("companies.list.data.lastConnection")}
              </StyledCellHeaderText>
            </StyledCell>
            <StyledCell>
              <StyledCellHeaderText variant='text2'>{""}</StyledCellHeaderText>
            </StyledCell>
          </StyledTableHeader>

          <StyledTable>
            {listOfAgents.map((profile, index) => (
              <StyledRowContainerLink key={"profile.id"}>
                <StyledCell>
                  <StyledCellText variant='text2'>
                    {profile.email}
                  </StyledCellText>
                </StyledCell>
                <StyledCell>
                  <StyledCellText variant='text2'>
                    {" "}
                    {profile.firstName} {profile?.lastName}
                  </StyledCellText>
                </StyledCell>
                <StyledCell>
                  <StyledCellText variant='text2'>{profile.iGG}</StyledCellText>
                </StyledCell>
                <StyledCell>
                  {/* {isRolesProfileCode(
                  profile?.Subscriptions?.[0]?.RolesProfileIdData?.code ?? "",
                ) ? (
                  <RolesProfileIcon
                    code={
                      (profile?.Subscriptions?.[0]?.RolesProfileIdData?.code as
                        | "ADMIN"
                        | "MANAGER"
                        | "ACCOUNTANT"
                        | "VIEWER") ?? ""
                    }
                    size={28}
                  />
                ) : null} */}
                  <RolesProfileIcon
                    code={(profile.role as "ADMIN" | "MANAGER") ?? ""}
                    size={28}
                  />
                  <StyledCellText variant='text2'>
                    {profile.role === "ADMIN" ? "Super agent" : "Agent"}
                    {/* {getUserRoleDisplayedName({
                    code: profile?.Subscriptions?.[0]?.RolesProfileIdData?.code,
                    name: profile?.Subscriptions?.[0]?.RolesProfileIdData?.name,
                    t: tCompanies,
                  })} */}
                  </StyledCellText>
                </StyledCell>
                <StyledCell>
                  <StyledCellText variant='text2'>
                    <BubbleText
                      text={capitalize(
                        tUsers(
                          `users.list.status.${profile.status}.label`.toLowerCase(),
                        ),
                      )}
                      variant={getStatus(profile.status)}
                    />
                  </StyledCellText>
                </StyledCell>
                <StyledCell>
                  <StyledCellText variant='text2'>
                    {" "}
                    {profile.createdAt}
                  </StyledCellText>
                </StyledCell>
                <StyledCell>
                  <StyledCellText variant='text2'>
                    {profile.lastConnection}
                  </StyledCellText>
                </StyledCell>

                <StyledCellContextualMenu>
                  <Text variant='text2'>
                    <ContextualMenu
                      dataTestId='contextual-menu'
                      open={index === menuOpen}
                      handleClose={() => {
                        setMenuOpen(-1);
                      }}
                      handleOpen={() => {
                        setMenuOpen(index);
                      }}
                      links={getLinks(profile.status, profile.email)}
                    />
                  </Text>
                </StyledCellContextualMenu>
              </StyledRowContainerLink>
            ))}
          </StyledTable>
        </StyledCustomTableContainer>
        {/* <Loading isLoading={false}>
            {totalNumberOfPages > 1 && currentPage < totalNumberOfPages ? (
              <StyledButtonContainer>
                <StyledButton
                  leftIcon={<Subtract {...iconProps} />}
                  variant='secondary'
                  buttonText={tCompanies("common.pagination.next_page", {
                    pageSize,
                  })}
                  isLowercase={true}
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                ></StyledButton>
              </StyledButtonContainer>
            ) : null}
          </Loading> */}
        {/* <StyledCurrentCountOnTotal
            currentCount={listOfUsers.length}
            total={totalNumberOfUsers}
            postFix={tCompanies("companies.list.accountsReceivable")}
          /> */}
      </RoundedTopLeftContainer>
    </>
  );
};

export default BackOffice;
